// Phone normalization utility
// Normalize phone numbers to +CC.NNN format as required by the API.
// Strips all non-digits from the local part and removes leading '0' (German-style).
// If inputs are missing, coerce to empty strings to avoid crashing.

export function formatPhone(countryCode: string, local: string): string {
  const cc = (countryCode ?? '').trim()
  const rawLocal = (local ?? '').trim()

  // Strip all non-digits from local part (API expects clean numbers)
  const digitsOnly = rawLocal.replace(/\D/g, '')

  // Remove leading zeros (German-style area code prefix). The backend
  // canonicalizes to no leading zeros — keep both sides identical so
  // cross-account phone comparisons match.
  const normalizedLocal = digitsOnly.replace(/^0+/, '')

  // Join with dot separator, avoid trailing/leading dots if one side is empty
  if (cc && normalizedLocal) return `${cc}.${normalizedLocal}`
  if (cc && !normalizedLocal) return cc
  if (!cc && normalizedLocal) return normalizedLocal
  return ''
}

// The canonical form the API accepts (mirrors backend internal/phone).
const CANONICAL_PHONE = /^\+\d{1,3}\.\d+$/

// Does a number already carry the canonical +CC.NNN form? Use it to gate a
// submit, since formatPhone returns a partial string (bare code, bare local,
// or empty) whenever one side is missing.
export function isCanonicalPhone(phone: string): boolean {
  return CANONICAL_PHONE.test(phone)
}

// Parse phone in +CC.NNN format back into components
export function parsePhone(phone: string): { countryCode: string; local: string } {
  if (!phone) return { countryCode: '+49', local: '' }

  // Handle +CC.NNN format (e.g., +49.891234567)
  if (phone.includes('.')) {
    const [cc, local] = phone.split('.', 2)
    return { countryCode: cc || '+49', local: local || '' }
  }

  // Handle +CCNNN format without dot (e.g., +49891234567)
  if (phone.startsWith('+')) {
    // Assume first 3 chars are country code for Germany (+49)
    return { countryCode: phone.slice(0, 3), local: phone.slice(3) }
  }

  // Default: treat as local number with German country code
  return { countryCode: '+49', local: phone }
}

// Parse pasted phone number and detect country code
// Uses a list of known calling codes to match properly
export function parsePastedPhone(
  pasted: string,
  defaultCountryCode: string = '+49',
  knownCallingCodes: string[] = []
): { countryCode: string; local: string } {
  // Remove spaces, dashes, parentheses but keep digits and +
  const cleaned = pasted.replace(/[\s\-()]/g, '')

  // Check for international format: +XX...
  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1) // Remove the +

    // Try to match against known calling codes (longest first)
    if (knownCallingCodes.length > 0) {
      // Sort by length descending to match longer codes first
      const sortedCodes = [...knownCallingCodes].sort((a, b) => b.length - a.length)
      for (const code of sortedCodes) {
        if (digits.startsWith(code)) {
          const local = digits.slice(code.length)
          return {
            countryCode: '+' + code,
            local: local.replace(/^0+/, '') // Remove leading zeros from local
          }
        }
      }
    }

    // Fallback: assume 2-digit country code (most common)
    if (digits.length >= 2) {
      return {
        countryCode: '+' + digits.slice(0, 2),
        local: digits.slice(2).replace(/^0+/, '')
      }
    }
  }

  // Check for 00XX format (international dialing)
  if (cleaned.startsWith('00')) {
    const withoutPrefix = cleaned.slice(2)
    return parsePastedPhone('+' + withoutPrefix, defaultCountryCode, knownCallingCodes)
  }

  // Local number with leading zeros - use default country
  if (cleaned.startsWith('0')) {
    return { countryCode: defaultCountryCode, local: cleaned.replace(/^0+/, '') }
  }

  // Plain number without prefix - use default country
  return { countryCode: defaultCountryCode, local: cleaned }
}

export default { formatPhone, isCanonicalPhone, parsePhone, parsePastedPhone }
